import Vue from "vue"
import {AxiosPromise} from "axios"
import {ApplicationEntity} from "@/model/entity/application-entity"
import {Filter} from "@/model/dto/filter"
import {Page} from "@/model/dto/page"
import {classToPlain, plainToClass} from "class-transformer"
import {ClassType} from "class-transformer/ClassTransformer"
import { DatatableSort } from "@/model/dto/datatable-sort"

export class EntityService<E extends ApplicationEntity> {
  protected path: string = ""

  protected $http = Vue.$http

  protected classType: ClassType<E>

  constructor(path: string, classType: ClassType<E>) {
    this.path = path
    this.classType = classType
  }

  saveBulk(entity: E[]): AxiosPromise<E[]> {
    entity = entity.map(item => plainToClass(this.classType, item))
    return this.$http.post(this.path + "/bulk_save", entity.map(item => classToPlain(item)))
  }

  getPage(page: number, rows: number, filters: Filter[], sort?: DatatableSort, isCodFilterEnabled?: boolean):
  AxiosPromise<Page<E>> {
    if (!!isCodFilterEnabled) {
      filters.push({field: "cod", value: isCodFilterEnabled})
    }
    return this.$http.get(this.path + "/page" + this.buildQueryParams(filters, page - 1, rows, sort)).then(response => {
      response.data.content = plainToClass(this.classType, response.data.content)
      return response
    })
  }

  getPagePickup(page: number, rows: number, filters: Filter[], sort?: DatatableSort): AxiosPromise<Page<E>> {
    return this.$http.get(this.path + "/page/pickup/" + this.buildQueryParams(filters, page - 1, rows, sort))
      .then(response => {
        response.data.content = plainToClass(this.classType, response.data.content)
        return response
    })
  }

  create(entity: E): AxiosPromise<E> {
    entity = trimDeep(plainToClass(this.classType, entity))
    return this.$http.post(this.path, classToPlain(entity))
  }

  modify(entity: E): AxiosPromise<E> {
    entity = trimDeep(plainToClass(this.classType, entity))
    return this.$http.put(this.path, classToPlain(entity))
  }

  createOrModify(entity: E): AxiosPromise<E> {
    if (exists(entity.id)) {
      return this.modify(entity)
    } else {
      return this.create(entity)
    }
  }

  delete(id: number): AxiosPromise<void> {
    return this.$http.delete(this.path + "/" + id)
  }

  getById(id: number): AxiosPromise<E> {
    return this.$http.get(this.path + "/" + id)
  }

  getAll(): AxiosPromise<E[]> {
    return this.$http.get(this.path + "/all")
  }

  export(): AxiosPromise<any> {
    return this.$http({
      method: "get",
      url: this.path + "/export/csv",
      responseType: "arraybuffer"
    })
  }
  exportWithFilters(filters: Filter[]): AxiosPromise<any> {
    return this.$http({
      method: "get",
      url: this.path + "/export/csv" + this.buildQueryParams(filters),
      responseType: "arraybuffer"
    })
  }

  public buildQueryParams(filters: Filter[], page?: number, rows?: number, sort?: DatatableSort) {
    let query = "?"
    if (exists(page)) {
      query += "page=" + page + "&"
    }
    if (exists(rows)) {
      query += "rows=" + rows + "&"
    }
    filters.forEach(filter => {
      const trimmed = trimDeep(filter)
      query += "filter=" + trimmed.field + ":" + trimmed.value + "&"
    })
    if (exists(sort)) {
      query += `sort=${sort!.prop}:${sort!.order}`
    }
    return query
  }
}
